import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import { Message } from "primereact/message";
import { Tooltip } from "primereact/tooltip";

import {
  actionTooltipProps,
  DisabledTooltip,
  EmptyState,
  ErrorState,
  ExportTableDialog,
  ForbiddenState,
  Icon,
  StateMessage,
  TABLE_ACTIONS_LABEL,
  TableActionsMenu,
  TableSettingsDialog,
} from "@/ds";
import { CancelarEscopoMacroDialog } from "./cancelar-escopo-macro-dialog";
import { CancelarTipoObraDialog } from "./cancelar-tipo-obra-dialog";
import { CotacaoCard } from "./cotacao-card";
import { DetalheEscopoMacro } from "./detalhe-escopo-macro";
import { AdicionarEscopoMacroDialog } from "./adicionar-escopo-macro-dialog";
import { TransferirTipoObraDialog } from "./transferir-tipo-obra-dialog";
import { NovoTipoObraDialog } from "./novo-tipo-obra-dialog";
import { OutlookCard } from "./outlook-card";
import { PlanejamentoTabela } from "./planejamento-tabela";
import { ProjecaoDialog } from "./projecao-dialog";
import { TransferirEscopoMacroDialog } from "./transferir-escopo-macro-dialog";
import {
  ADICIONAR_LABEL,
  CARREGANDO_LABEL,
  EXPORTACAO_LIST_NAME,
  OUTLOOK_TITLE,
  PENDENCIAS_LABEL,
} from "./planejamento-controle.constants";
import styles from "./planejamento-controle.module.scss";
import type { PlanejamentoControleViewModel } from "./types-planejamento-controle";

/** Aba Planejamento e Controle: carregando, sem permissão, erro, vazio (obra sem Tipos) ou cotação, cards do Outlook e a tabela hierárquica. */
export function PlanejamentoControleView({
  estado,
  errorMessage,
  recarregar,
  cards,
  cotacaoCard,
  linhas,
  competencias,
  colunasTabela,
  pendencias,
  tabelaCompacta,
  abrirExportacao,
  abrirConfiguracaoTabela,
  exportacao,
  configuracaoTabela,
  vazio,
  menuRef,
  menuItens,
  adicionarAberto,
  mostrarMenu,
  fecharMenu,
  teclasMenu,
  alternar,
  abrirDetalhe,
  fecharDetalhe,
  detalhe,
  projecao,
  fecharProjecao,
  concluirProjecao,
  cotacaoAlterada,
  novoTipo,
  fecharNovoTipo,
  novoEscopo,
  fecharNovoEscopo,
  concluirNovoEscopo,
  concluirNovoTipo,
  cancelarEscopo,
  fecharCancelarEscopo,
  concluirCancelarEscopo,
  transferirEscopo,
  fecharTransferirEscopo,
  concluirTransferirEscopo,
  transferirTipo,
  fecharTransferirTipo,
  concluirTransferirTipo,
  recarregarComposicao,
  cancelarTipo,
  fecharCancelarTipo,
  concluirCancelarTipo,
  abrirMenuLinha,
  abrirMenuAdicionar,
}: PlanejamentoControleViewModel) {
  if (estado === "carregando") {
    return (
      <div className={styles.estado}>
        <StateMessage variant="loading" title={CARREGANDO_LABEL} testId="planejamento-loading" />
      </div>
    );
  }

  if (estado === "proibido") {
    return (
      <div className={styles.estado}>
        <ForbiddenState description={errorMessage ?? undefined} testId="planejamento-error" />
      </div>
    );
  }

  if (estado === "erro") {
    return (
      <div className={styles.estado}>
        <ErrorState
          message={errorMessage ?? undefined}
          onRetry={recarregar}
          testId="planejamento-error"
        />
      </div>
    );
  }

  const adicionar = (
    <Button
      type="button"
      severity="secondary"
      label={ADICIONAR_LABEL}
      icon={<Icon name="plus" size={14} />}
      iconPos="left"
      aria-haspopup="menu"
      aria-expanded={adicionarAberto}
      onClick={abrirMenuAdicionar}
      data-testid="planejamento-adicionar"
    >
      {/* O DS não tem angle-down/up: PrimeIcons (GLOSSARIO, Icon "PrimeIcons só sem desenho"). */}
      <i className={adicionarAberto ? "pi pi-angle-up" : "pi pi-angle-down"} aria-hidden />
    </Button>
  );

  return (
    <div className={styles.aba} data-testid="planejamento-controle">
      {cotacaoCard ? <CotacaoCard {...cotacaoCard} /> : null}

      {/* Figma `plan-controle/controle-outlook`: título, cards e o alerta de pendências, a 16 entre si. */}
      <div className={styles.bloco}>
        <div className={styles.secao}>
          <h2 className={styles.secaoTitulo}>{OUTLOOK_TITLE}</h2>
          <span className={styles.divisor} aria-hidden />
        </div>
        <div className={styles.cards}>
          {cards.map((card) => (
            <OutlookCard key={card.chave} card={card} />
          ))}
        </div>

        {pendencias.length > 0 ? (
          <div className={styles.pendencias} role="group" aria-label={PENDENCIAS_LABEL}>
            {pendencias.map((pendencia) => (
              <Message
                key={pendencia}
                severity="warn"
                icon={<Icon name="exclamation-triangle" size={18} />}
                text={pendencia}
                className="p-message-block"
                pt={{ text: { className: styles.pendenciaTexto } }}
                data-testid="planejamento-pendencia"
              />
            ))}
          </div>
        ) : null}
      </div>

      {/* Figma `plan-controle/tabela-tipos-obra-escopos`: a barra do Adicionar e a tabela, a 16 entre si. */}
      <div className={styles.bloco}>
        <div className={styles.barra}>
          {adicionar}
          {/* Figma 16437:719044 (⋮) e 17669:845680 (menu Opções da Tabela). */}
          <TableActionsMenu.Root testId="planejamento-opcoes">
            <TableActionsMenu.Trigger />
            <TableActionsMenu.ExportItem onClick={abrirExportacao} />
            <TableActionsMenu.SettingsItem onClick={abrirConfiguracaoTabela} />
          </TableActionsMenu.Root>
          {/* O `tooltip` do Button não pega no Trigger (a ref da raiz vai pelo `pt`): o Tooltip mira o botão pelo testid. */}
          <Tooltip
            target='[data-testid="planejamento-opcoes-trigger"]'
            {...actionTooltipProps(TABLE_ACTIONS_LABEL, { event: "hover" })}
          />
        </div>

        {estado === "vazio" ? (
          <EmptyState.Root size="sm" testId="planejamento-empty">
            <EmptyState.Title>{vazio.titulo}</EmptyState.Title>
            <EmptyState.Description>{vazio.descricao}</EmptyState.Description>
          </EmptyState.Root>
        ) : (
          <PlanejamentoTabela
            linhas={linhas}
            competencias={competencias}
            compacta={tabelaCompacta}
            colunasTabela={colunasTabela}
            alternar={alternar}
            abrirDetalhe={abrirDetalhe}
            abrirMenu={abrirMenuLinha}
          />
        )}
      </div>

      <span {...teclasMenu}>
        <Menu
          popup
          ref={menuRef}
          popupAlignment="right"
          onShow={mostrarMenu}
          onHide={fecharMenu}
          model={menuItens.map((item) => ({
            label: item.label,
            disabled: item.disabled,
            command: item.onSelect,
            template: (menuItem, options) => (
              <div
                className={item.disabled ? "p-menuitem-content p-disabled" : "p-menuitem-content"}
              >
                <DisabledTooltip
                  content={item.motivo}
                  position="left"
                  className={styles.itemMenuAlvo}
                >
                  <a
                    className={`${options.className} ${styles.itemMenu}`}
                    role="menuitem"
                    aria-disabled={item.disabled || undefined}
                    onClick={item.disabled ? undefined : options.onClick}
                  >
                    {item.icone && "ds" in item.icone ? (
                      <Icon name={item.icone.ds} size={14} />
                    ) : null}
                    {item.icone && "prime" in item.icone ? (
                      <i className={item.icone.prime} aria-hidden />
                    ) : null}
                    <span className={options.labelClassName}>{menuItem.label}</span>
                  </a>
                </DisabledTooltip>
              </div>
            ),
          }))}
          data-testid="planejamento-menu"
        />
      </span>

      {exportacao.aberta ? (
        <ExportTableDialog
          listName={EXPORTACAO_LIST_NAME}
          isExporting={exportacao.exportando}
          onExport={exportacao.exportar}
          onCancel={exportacao.fechar}
          testId="planejamento-exportar"
        />
      ) : null}
      {/* Figma 16711:735430: colunas todas marcadas e desabilitadas (tabela de cálculos); só o tamanho muda. */}
      <TableSettingsDialog
        {...configuracaoTabela}
        showColumnsSection
        lockedColumnsDisabled
        testId="planejamento-configuracao"
      />

      {novoEscopo ? (
        <AdicionarEscopoMacroDialog
          {...novoEscopo}
          onClose={fecharNovoEscopo}
          onConcluido={concluirNovoEscopo}
          onCotacaoAlterada={cotacaoAlterada}
        />
      ) : null}
      {novoTipo ? (
        <NovoTipoObraDialog {...novoTipo} onClose={fecharNovoTipo} onConcluido={concluirNovoTipo} />
      ) : null}
      {cancelarEscopo ? (
        <CancelarEscopoMacroDialog
          {...cancelarEscopo}
          onClose={fecharCancelarEscopo}
          onConcluido={concluirCancelarEscopo}
        />
      ) : null}
      {transferirEscopo ? (
        <TransferirEscopoMacroDialog
          {...transferirEscopo}
          onClose={fecharTransferirEscopo}
          onConcluido={concluirTransferirEscopo}
        />
      ) : null}
      {transferirTipo ? (
        <TransferirTipoObraDialog
          {...transferirTipo}
          onClose={fecharTransferirTipo}
          onConcluido={concluirTransferirTipo}
          onRecarregar={recarregarComposicao}
        />
      ) : null}
      {cancelarTipo ? (
        <CancelarTipoObraDialog
          {...cancelarTipo}
          onClose={fecharCancelarTipo}
          onConcluido={concluirCancelarTipo}
        />
      ) : null}
      {detalhe ? <DetalheEscopoMacro {...detalhe} onClose={fecharDetalhe} /> : null}
      {projecao ? (
        <ProjecaoDialog
          {...projecao}
          onClose={fecharProjecao}
          onConcluido={concluirProjecao}
          onCotacaoAlterada={cotacaoAlterada}
        />
      ) : null}
    </div>
  );
}
